import { ajvResolver } from '@hookform/resolvers/ajv'
import { ArrowBack, EmailOutlined } from '@mui/icons-material'
import { Box, Button, CircularProgress, Grid, Link, TextField } from '@mui/material'
import ImageBanner from 'components/image-banner'
import LogoTheme from 'components/logo-theme'
import PasswordBannerSVG from 'components/svg/password-banner'
import WrapperPermission from 'components/wrapper-permission'
import { useAlert } from 'contexts/alert'
import { useCustomTheme } from 'contexts/theme'
import React, { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import ForgotPasswordSchema, { IForgotPasswordFormData } from 'schema/authentication/forgot-password'
import api from 'services/api'
import { useStyles } from './styles'

const ForgotPassword: React.FC = () => {
  const { closeAlert, openAlert } = useAlert()
  const { theme } = useCustomTheme()
  const history = useHistory()
  const classes = useStyles()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<IForgotPasswordFormData>({
    resolver: ajvResolver(ForgotPasswordSchema),
  })

  const onSubmit = useCallback(
    async (data: IForgotPasswordFormData) => {
      try {

        await api.post('/passwords/forgot', {
          email: data.email,
        })  
      } catch (err) {
        console.log(err)
        await new Promise((resolve) => setTimeout(() => resolve(''), 2000))
      } finally {
        openAlert(
          'Em breve, você receberá um e-mail com instruções para redefinir sua senha, caso o e-mail fornecido esteja associado à sua conta.',
          'success'
        )
      }
    },
    [history]
  )

  return (
    <Box className={classes.container}>
      <Box className={classes.mainWrapper}>
        <WrapperPermission>
          <Box
            component='form'
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            data-testid='form'
            className={classes.formBox}
          >
            <LogoTheme />

            <h1 className={classes.formTitle}>Esqueceu sua senha?</h1>
            <span className={classes.formSubTitle}>
              Não se preocupe, basta digitar o endereço de e-mail que você usou quando se inscreveu e enviaremos
              instruções para redefinir sua senha.
            </span>

            <TextField
              required
              autoFocus
              id='email'
              type='email'
              label='E-mail'
              variant='outlined'
              placeholder='Digite seu endereço de e-mail'
              disabled={isSubmitting}
              error={Boolean(errors.email)}
              style={{ marginBottom: '5px', marginTop: '20px' }}
              helperText={errors?.email?.message ?? ''}
              {...register('email')}
              InputProps={{
                startAdornment: (
                  <EmailOutlined
                    color={isSubmitting ? 'disabled' : 'inherit'}
                    sx={{ marginRight: '12px' }}
                  />
                ),
              }}
            />

            <Button
              fullWidth
              size='large'
              type='submit'
              variant='contained'
              sx={{ mt: 2, mb: 1 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress
                  color='inherit'
                  size={26}
                />
              ) : (
                'ENVIAR INSTRUÇÕES'
              )}
            </Button>

            <Grid container>
              <Grid
                item
                xs={12}
              >
                <Link
                  href='/'
                  variant='inherit'
                  data-testid='signup-link'
                  className={classes.signupLink}
                >
                  <ArrowBack
                    color='inherit'
                    sx={{ marginRight: '8px' }}
                  />
                  Voltar para a página de Login
                </Link>
              </Grid>
            </Grid>
          </Box>
        </WrapperPermission>

        <ImageBanner
          title='Crie uma nova senha para sua conta na Conducco.'
          subTitle='Insira o email associado à sua conta Conducco. Após isso, você receberá um email com um link para redefinir sua senha. Clique no link fornecido no email para acessar a tela de criação de nova senha e, em seguida, poderá acessar novamente a sua conta.'
          banner={<PasswordBannerSVG />}
        />
      </Box>
    </Box>
  )
}

export default ForgotPassword
